import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Calendar, Mail, MapPin } from 'lucide-react';
import { getEnvironmentConfig } from '@safermark/config';

export default function TermsPage() {
  const config = getEnvironmentConfig();
  const currentDate = new Date().toLocaleDateString('en-NZ', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-black to-slate-900">
      {/* Header */}
      <div className="relative bg-black/50 border-b border-cyan-400/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link 
                to="/" 
                className="flex items-center text-cyan-400 hover:text-cyan-300 transition-colors duration-200"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Home
              </Link>
            </div>
            <div className="flex items-center space-x-2">
              <Shield className="h-6 w-6 text-cyan-400" />
              <span className="font-bold text-white">Safermark Vaults</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-black/40 backdrop-blur-sm border border-cyan-400/30 rounded-2xl p-8 lg:p-12">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-white mb-4">Terms and Conditions</h1>
            <div className="flex items-center space-x-4 text-gray-400">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                <span>Last updated: {currentDate}</span>
              </div>
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-2" />
                <span>New Zealand</span>
              </div>
            </div>
          </div>

          <div className="prose prose-invert prose-cyan max-w-none">
            <div className="space-y-8 text-gray-300 leading-relaxed">
              
              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">1. Acceptance of Terms</h2>
                <p>
                  By accessing or using Safermark Vaults ("the Service"), you agree to be bound by these Terms and Conditions ("Terms"). 
                  If you disagree with any part of these terms, then you may not access the Service. These Terms constitute a legally binding 
                  agreement between you and Safermark Vaults, operated by a New Zealand registered company providing services globally.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">2. Description of Service</h2>
                <p>
                  Safermark Vaults is a secure bookmark and note management service that provides:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>End-to-end encrypted storage of bookmarks and notes</li>
                  <li>Chrome browser extension for easy access</li>
                  <li>Cloud synchronization for premium subscribers</li>
                  <li>Zero-knowledge architecture ensuring your data remains private</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">3. User Accounts and Registration</h2>
                <p>
                  To access certain features of the Service, you must create an account. You agree to:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Provide accurate, current, and complete information during registration</li>
                  <li>Maintain the security of your password and account</li>
                  <li>Immediately notify us of any unauthorized use of your account</li>
                  <li>Accept responsibility for all activities that occur under your account</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">4. Subscription and Payment Terms</h2>
                <div className="space-y-3">
                  <p>
                    Safermark Vaults offers both free and premium subscription tiers:
                  </p>
                  <h4 className="text-lg font-medium text-white">Free Tier:</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Local storage via browser extension</li>
                    <li>Basic encryption features</li>
                    <li>No cloud synchronization</li>
                  </ul>
                  
                  <h4 className="text-lg font-medium text-white">Premium Tier:</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Cloud synchronization across devices</li>
                    <li>Fair usage on cloud vault storage</li>
                    <li>Priority support</li>
                    <li>Advanced features and integrations</li>
                  </ul>

                  <p>
                    Premium subscriptions are billed monthly or annually. Payment processing is handled by Stripe. 
                    All prices are displayed in US Dollars (USD) unless otherwise specified in your local currency.
                  </p>
                  
                  <p>
                    You may cancel your subscription at any time. Upon cancellation, you will retain access to premium 
                    features until the end of your current billing period.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">5. Data Security and Privacy</h2>
                <p>
                  We take data security seriously and implement industry-standard security measures:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>AES-256 encryption for all stored data</li>
                  <li>Zero-knowledge architecture - we cannot access your encrypted data</li>
                  <li>Secure transmission using HTTPS/TLS</li>
                  {/*<li>Regular security audits and updates</li>*/}
                </ul>
                <p className="mt-3">
                  For detailed information about data handling, please review our{' '}
                  <Link to="/privacy" className="text-cyan-400 hover:text-cyan-300 underline">
                    Privacy Policy
                  </Link>.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">6. Acceptable Use</h2>
                <p>
                  You agree not to use the Service to:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Store or transmit illegal, harmful, or offensive content</li>
                  <li>Violate any applicable laws or regulations</li>
                  <li>Infringe on intellectual property rights of others</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Distribute malware, viruses, or other harmful code</li>
                  <li>Use the Service for commercial purposes beyond personal use</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">7. Intellectual Property</h2>
                <p>
                  The Service and its original content, features, and functionality are and will remain the exclusive 
                  property of Safermark Vaults and its licensors. The Service is protected by copyright, trademark, 
                  and other laws. You retain ownership of your data stored within the Service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">8. Service Availability</h2>
                <p>
                  We strive to maintain high availability of our Service but cannot guarantee uninterrupted access. 
                  We reserve the right to modify, suspend, or discontinue the Service with reasonable notice. 
                  We are not liable for any interruption of service or loss of data.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">9. Limitation of Liability</h2>
                <p>
                  To the maximum extent permitted by New Zealand law, Safermark Vaults shall not be liable for any 
                  indirect, incidental, special, consequential, or punitive damages, including without limitation, 
                  loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the Service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">10. Termination</h2>
                <p>
                  We may terminate or suspend your account immediately, without prior notice or liability, for any 
                  reason whatsoever, including without limitation if you breach the Terms. Upon termination, your 
                  right to use the Service will cease immediately.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">11. Governing Law and Disputes</h2>
                <p>
                  These Terms shall be governed and construed in accordance with the laws of New Zealand. 
                  Any disputes arising from these Terms or your use of the Service shall be subject to the 
                  exclusive jurisdiction of the courts of New Zealand.
                </p>
                <p className="mt-3">
                  However, this does not affect your statutory rights under the consumer protection laws 
                  of your country of residence, where applicable.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">12. Changes to Terms</h2>
                <p>
                  We reserve the right, at our sole discretion, to modify or replace these Terms at any time. 
                  If a revision is material, we will try to provide at least 30 days notice prior to any new terms taking effect.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">13. Contact Information</h2>
                <div className="bg-slate-800/50 rounded-lg p-4 border border-cyan-400/20">
                  <p>If you have any questions about these Terms and Conditions, please contact us:</p>
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-cyan-400" />
                      <span>{config.contactEmail}</span>
                    </div>
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 mr-2 text-cyan-400" />
                      <span>New Zealand</span>
                    </div>
                  </div>
                </div>
              </section>

              <div className="border-t border-gray-600 pt-6 mt-8">
                <p className="text-sm text-gray-400">
                  These Terms and Conditions are effective as of {currentDate}. 
                  By continuing to use Safermark Vaults, you acknowledge that you have read, 
                  understood, and agree to be bound by these terms.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}